import { memo, useMemo, useRef, useState, useEffect } from "react";
import { Box, Button, LinearProgress, Paper, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { useAnalysisStore, type AnalysisJob } from "./analysis.store";
import type { AnalysisProgress } from "./types";
import { StatusBadge } from "@/shared/components/StatusBadge";
import { formatAnalysisDuration } from "./analysis-format";
import { analysisProgressErrorCopy } from "./analysis-progress-copy";

const CATCHY_MESSAGES = [
  "Nikola is analyzing your moves…",
  "Nikola is calculating deep positions…",
  "Nikola is finding the best continuations…",
  "Nikola is hunting for tactics…",
  "Nikola is evaluating candidate moves…",
  "Nikola is searching for improvements…",
  "Nikola is analyzing like a GM…",
  "Nikola is computing variations…",
];

const AnimatedNodes = ({ nodes }: { nodes: number }) => {
  const [displayedNodes, setDisplayedNodes] = useState(0);
  const displayedNodesRef = useRef(0);
  const lastProgressAtRef = useRef<number | null>(null);

  useEffect(() => {
    const start = displayedNodesRef.current;
    const target = Math.max(start, nodes);
    if (target === start) return;

    const startedAt = performance.now();
    const previousProgressAt = lastProgressAtRef.current ?? startedAt;
    const updateGap = startedAt - previousProgressAt;
    const valueGap = target - start;
    const duration = Math.min(3_000, Math.max(420, updateGap * 8, (valueGap / 50_000) * 1_000));
    lastProgressAtRef.current = startedAt;
    let frameId: number;

    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - startedAt) / duration);
      const next = Math.min(target, Math.floor(start + (target - start) * elapsed));
      displayedNodesRef.current = next;
      setDisplayedNodes(next);
      if (elapsed < 1) frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [nodes]);

  return (
    <Typography
      variant="caption"
      sx={{
        fontSize: "0.7rem",
        flexShrink: 0,
        animation: "fadeIn 0.3s ease-in",
        "@keyframes fadeIn": {
          from: { opacity: 0, transform: "translateY(2px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {formatNodes(displayedNodes)} positions analyzed
    </Typography>
  );
};

interface AnalysisProgressPanelProps {
  gameId?: string;
  dense?: boolean;
}

export const AnalysisProgressPanel = ({ gameId, dense = false }: AnalysisProgressPanelProps) => {
  const jobs = useAnalysisStore((state) => state.jobs);
  const isRunning = useAnalysisStore((state) => state.isRunning);
  const gamesCompleted = useAnalysisStore((state) => state.gamesCompleted);
  const gamesTotal = useAnalysisStore((state) => state.gamesTotal);
  const downloadPercent = useAnalysisStore((state) => state.downloadPercent);
  const enginePhase = useAnalysisStore((state) => state.enginePhase);
  const initError = useAnalysisStore((state) => state.initError);
  const preset = useAnalysisStore((state) => state.preset);

  const scopedJobs = useMemo(() => (gameId ? jobs.filter((job) => job.gameId === gameId) : jobs), [gameId, jobs]);
  const currentJob = scopedJobs.find((job) => job.status === "analyzing");
  const currentProgress = useAnalysisStore((state) => (currentJob ? state.progressByGameId[currentJob.gameId] : null));
  const overallPercent = gameId
    ? scopedJobs[0]?.status === "done"
      ? 100
      : scopedJobs[0]?.status === "analyzing"
        ? 50
        : 0
    : gamesTotal > 0
      ? (gamesCompleted / gamesTotal) * 100
      : 0;
  const movePercent = currentProgress ? (currentProgress.currentMove / currentProgress.totalMoves) * 100 : 0;
  const pad = dense ? 1.5 : 2;
  const awaitingJob = !!gameId && scopedJobs.length === 0 && downloadPercent == null && !initError;

  return (
    <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5, overflowX: "clip" }}>
      {awaitingJob && (
        <StatusPaper pad={pad}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Spinner size={14} />
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Starting analysis...
            </Typography>
          </Box>
        </StatusPaper>
      )}

      {downloadPercent != null && (
        <StatusPaper pad={pad}>
          <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, mb: 0.5, display: "block" }}>
            {enginePhase ?? "Loading engine..."}
          </Typography>
          <LinearProgress
            variant={downloadPercent > 0 ? "determinate" : "indeterminate"}
            value={downloadPercent}
            sx={{ height: 6, bgcolor: "background.default" }}
          />
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
            {downloadPercent}% complete. {preset.useFullEngine ? "108 MB download" : "7 MB download"}
          </Typography>
        </StatusPaper>
      )}

      {initError && (
        <Paper sx={{ p: pad, bgcolor: "background.paper", border: "2px solid", borderColor: "secondary.main" }}>
          <Typography variant="body2" sx={{ color: "secondary.main", fontWeight: 600 }}>
            {initError}
          </Typography>
        </Paper>
      )}
      {isRunning && scopedJobs.length > 0 && (
        <StatusPaper pad={pad}>
          {!gameId && (
            <>
              <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700, mb: 0.5, display: "block" }}>
                Overall: {gamesCompleted}/{gamesTotal} games
              </Typography>
              <LinearProgress
                aria-label="Nikola analysis progress"
                variant="determinate"
                value={overallPercent}
                sx={{ height: 8, mb: 1.5, bgcolor: "background.default" }}
              />
            </>
          )}
          {currentJob && (
            <>
              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {gameId ? "Analyzing" : `vs ${currentJob.opponent}`}:{" "}
                  {currentProgress?.phase === "continuations"
                    ? "Computing best lines"
                    : currentProgress?.phase === "alternatives"
                      ? "Finding alternatives for mistakes"
                      : "Analyzing moves"}
                </Typography>
                <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 700 }}>
                  {currentProgress?.currentMove ?? 0}/{currentProgress?.totalMoves ?? "?"} moves
                </Typography>
              </Box>
              {currentProgress?.currentSan && (
                <Typography variant="caption" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                  Evaluating: {currentProgress.currentSan} (depth {currentProgress.depth ?? preset.depth})
                </Typography>
              )}
              <LinearProgress
                aria-label="Nikola move analysis progress"
                variant="determinate"
                value={movePercent}
                sx={{ height: 6, bgcolor: "background.default", mb: 1 }}
              />
              {currentProgress && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    fontSize: "0.7rem",
                    color: "text.secondary",
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 0,
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    {currentProgress.nodes ? <AnimatedNodes nodes={currentProgress.nodes} /> : <DottedAnimation />}
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {currentProgress.startTime && (
                        <Typography variant="caption" sx={{ fontSize: "0.7rem", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {getEstimatedRemaining(currentProgress)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      opacity: 0,
                      height: 0,
                      overflow: "hidden",
                      transition: "opacity 0.3s ease, height 0.3s ease",
                      "&.show": { opacity: 1, height: "auto" },
                    }}
                    className="show"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.65rem",
                        color: "rgba(255,255,255,0.4)",
                        fontStyle: "italic",
                        animation: "slideIn 0.4s ease 0.1s both",
                        "@keyframes slideIn": {
                          from: { opacity: 0, transform: "translateY(-2px)" },
                          to: { opacity: 1, transform: "translateY(0)" },
                        },
                      }}
                    >
                      <CatchyMessage />
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}
        </StatusPaper>
      )}

      {gameId && scopedJobs[0] && (scopedJobs[0].status === "done" || scopedJobs[0].status === "error") && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {scopedJobs[0].status === "done" && scopedJobs[0].result ? (
            <>
              <CheckCircleIcon sx={{ color: "primary.main", fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 700 }}>
                Analysis complete
              </Typography>
            </>
          ) : (
            <>
              <ErrorIcon sx={{ color: "secondary.main", fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: "secondary.main" }}>
                {analysisProgressErrorCopy(scopedJobs[0].error)}
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export const AnalysisJobRow = memo(function AnalysisJobRow({
  job,
  isRunning,
  onRetry,
}: {
  job: AnalysisJob;
  isRunning: boolean;
  onRetry: (job: AnalysisJob) => void;
}) {
  const current = job.status === "analyzing";

  return (
    <Paper
      sx={{
        mb: 0.5,
        border: current ? "2px solid" : "1px solid",
        borderColor: current ? "primary.main" : "divider",
        bgcolor: "background.default",
        backgroundImage: "none",
        contain: "layout paint style",
        minHeight: 70,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          minHeight: 68,
          boxSizing: "border-box",
          cursor: "default",
        }}
      >
        {job.status === "done" && <CheckCircleIcon sx={{ color: "primary.main", fontSize: 20 }} />}
        {job.status === "error" && <ErrorIcon sx={{ color: "secondary.main", fontSize: 20 }} />}
        {job.status === "analyzing" && <Spinner size={20} />}
        {job.status === "queued" && (
          <Box sx={{ width: 20, height: 20, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.2)" }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: current ? 700 : 600 }}>
            vs {job.opponent}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            {job.status === "done" && job.result && (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {formatDuration(job.durationMs)} · {job.preset.label}
                </Typography>
                {job.resultLabel === "kept-stronger" && <StatusBadge label="Stronger saved analysis kept" />}
              </>
            )}
            {job.status === "analyzing" && <JobProgress gameId={job.gameId} />}
            {job.status === "error" && (
              <>
                <Typography variant="caption" sx={{ color: "secondary.main", lineHeight: 1.35 }}>
                  Failed analysis · {analysisProgressErrorCopy(job.error)}
                </Typography>
                <Button
                  size="small"
                  disabled={isRunning}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRetry(job);
                  }}
                >
                  Retry
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
});

const JobProgress = ({ gameId }: { gameId: string }) => {
  const progress = useAnalysisStore((state) => state.progressByGameId[gameId]);
  return (
    <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 600 }}>
      {progress?.currentMove ?? 0}/{progress?.totalMoves ?? "?"} moves
    </Typography>
  );
};

const formatDuration = (durationMs: number | null): string =>
  durationMs == null ? "Completed" : formatAnalysisDuration(durationMs);

const StatusPaper = ({ pad, children }: { pad: number; children: React.ReactNode }) => (
  <Paper
    sx={{ p: pad, bgcolor: "background.paper", backgroundImage: "none", border: "2px solid", borderColor: "divider" }}
  >
    {children}
  </Paper>
);

const formatNodes = (nodes: number): string => {
  if (nodes >= 1_000_000_000) return `${(nodes / 1_000_000_000).toFixed(1)}B`;
  if (nodes >= 1_000_000) return `${(nodes / 1_000_000).toFixed(1)}M`;
  if (nodes >= 1_000) return `${(nodes / 1_000).toFixed(1)}K`;
  return nodes.toString();
};

const getEstimatedRemaining = (progress: AnalysisProgress): string => {
  if (!progress.startTime || progress.currentMove === 0) return "";
  const elapsed = Date.now() - progress.startTime;
  const avgPerMove = elapsed / progress.currentMove;
  const remaining = progress.totalMoves - progress.currentMove;
  const estimatedMs = remaining * avgPerMove;
  const totalSec = Math.floor(estimatedMs / 1000);

  if (totalSec <= 0) return "Nearly there…";
  if (totalSec < 3) return "Wrapping up…";

  return `${formatAnalysisDuration(estimatedMs, true)} left`;
};

const DottedAnimation = () => {
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <Typography variant="caption" sx={{ fontSize: "0.7rem", flexShrink: 0 }}>
      Analyzing{"...".substring(0, dots)}
    </Typography>
  );
};

const CatchyMessage = () => {
  const [message, setMessage] = useState(CATCHY_MESSAGES[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessage(CATCHY_MESSAGES[Math.floor(Math.random() * CATCHY_MESSAGES.length)]);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return <>{message}</>;
};

const Spinner = ({ size }: { size: number }) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      border: "2px solid",
      borderColor: "primary.main",
      borderTopColor: "transparent",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      "@keyframes spin": { to: { transform: "rotate(360deg)" } },
    }}
  />
);
